import { zodResolver } from '@hookform/resolvers/zod'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { z } from 'zod'

import Banner from '@/components-latest/primitives/Banner'
import Button from '@/components-latest/primitives/Button'
import Input from '@/components-latest/primitives/Controls/Input'
import PasswordInput from '@/components-latest/primitives/Controls/PasswordInput'
import { AlertCircle, InfoCircle } from '@/components-latest/primitives/Icons'
import Text from '@/components-latest/primitives/Text'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components-latest/shared/Form'
import i18n from '@/config/i18n'
import history from '@/config/router/history'
import { PATHS } from '@/config/router/routes'
import { DENIED_STORE_STATUSES } from '@/constants/common'
import { sendLoginDeniedEvent } from '@/ducks/app/helpers'
import { useLoginMutation } from '@/ducks/auth/api/auth.api'
import {
  selectIsBlocked,
  selectLoginThrottle
} from '@/ducks/auth/loginThrottle.selectors'
import {
  incrementFailedAttempts,
  resetLoginThrottle
} from '@/ducks/auth/slice/loginThrottle.slice'
import { setLoginDeniedStatus } from '@/ducks/store/slice/store.slice'

import Styles from './LoginForm.module.scss'

const formSchema = z.object({
  email: z.string().email(i18n.t('login.error.email.incorrect')),
  password: z.string().min(1, i18n.t('login.error.password.empty'))
})
type FormData = z.infer<typeof formSchema>

const LoginForm = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isBlocked = useSelector(selectIsBlocked)
  const { failedAttempts, blockedUntil } = useSelector(selectLoginThrottle)
  const [login, { isLoading }] = useLoginMutation()

  const [remainingTime, setRemainingTime] = React.useState('')

  React.useEffect(() => {
    if (!blockedUntil) return

    const interval = setInterval(() => {
      const now = Date.now()
      const target = new Date(blockedUntil).getTime()
      const remaining = Math.max(target - now, 0)

      const minutes = Math.floor(remaining / 60000)
      const seconds = Math.floor((remaining % 60000) / 1000)

      setRemainingTime(`${minutes}m ${seconds}s`)

      if (remaining <= 0) {
        clearInterval(interval)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [blockedUntil])
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      email: '',
      password: ''
    }
  })

  const handleSubmit = form.handleSubmit(data => {
    login({ ...data })
      .unwrap()
      .then(() => {
        dispatch(resetLoginThrottle())
      })
      .catch(error => {
        dispatch(incrementFailedAttempts())
        const errorFlag = error?.error_flag ?? 'unknown'
        const errorMessage = error?.message || t('login.error.generic')
        form.setError('root.serverError', {
          type: errorFlag,
          message: errorMessage
        })

        if (DENIED_STORE_STATUSES.includes(errorFlag)) {
          dispatch(setLoginDeniedStatus({ status: errorFlag }))
          setTimeout(() => history.push(PATHS.LOGIN_DENIED), 50)

          const storeId = error?.store_id
          sendLoginDeniedEvent(errorFlag, storeId)
        }
      })
  })

  const hasServerError = !!form.formState.errors?.root?.serverError

  return (
    <div data-testid="login-form">
      {hasServerError && (
        <Banner
          className={Styles.errorBanner}
          state="negative"
          type="boxed"
          description={
            form.formState.errors?.root?.serverError?.type
              ? t(
                  `login.error_flag_${form.formState.errors.root.serverError.type}`
                )
              : ''
          }
          icon={<InfoCircle />}
          showCloseButton
          onClose={() => form.clearErrors('root.serverError')}
        />
      )}
      <Form {...form}>
        <form className={Styles.form} onSubmit={handleSubmit}>
          <FormField
            name="email"
            control={form.control}
            render={({ field, fieldState }) => {
              const hasError = !!fieldState.error?.message
              return (
                <FormItem>
                  <FormLabel layout="column">
                    <Text variant="nextSubtitle" weight="bold" asChild>
                      <span>Email address</span>
                    </Text>
                    <FormControl>
                      <div className={Styles.inputWrapper}>
                        <Input
                          {...field}
                          placeholder={t('login.email.placeholder')}
                          className={Styles.input}
                          intent={hasError ? 'negative' : 'default'}
                        />
                        <div className={Styles.iconWrapper}>
                          {hasError && <AlertCircle width={20} height={20} />}
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage>
                      {fieldState.error?.message ? (
                        fieldState.error.message
                      ) : (
                        <>&nbsp;</>
                      )}
                    </FormMessage>
                  </FormLabel>
                </FormItem>
              )
            }}
          />

          <FormField
            name="password"
            control={form.control}
            render={({ field, fieldState }) => {
              const hasError = !!fieldState.error?.message

              return (
                <FormItem>
                  <FormLabel layout="column">
                    <Text variant="nextSubtitle" weight="bold" asChild>
                      <span>Password</span>
                    </Text>
                    <FormControl>
                      <PasswordInput
                        {...field}
                        intent={hasError ? 'negative' : 'default'}
                        className={Styles.input}
                        placeholder={
                          hasError ? t('login.password.placeholder') : ''
                        }
                      />
                    </FormControl>
                    <FormMessage>
                      {fieldState.error?.message ? (
                        fieldState.error.message
                      ) : (
                        <>&nbsp;</>
                      )}
                    </FormMessage>
                  </FormLabel>
                </FormItem>
              )
            }}
          />

          <Button
            variant="branded"
            size="large"
            loading={isLoading}
            className={Styles.rounded}>
            {t('login.ctas.login')}
          </Button>
        </form>
      </Form>
    </div>
  )
}

export default LoginForm
